/**
 * The particle path's source records — ENHANCED EFFECTS' half of
 * `frame-signals.ts`, split out when that file passed aislop's 400-line ceiling
 * and along the seam it already had.
 *
 * `frame-signals.ts` next door is the **sprite and audio** half: which entities
 * a frame's `SMOKE.BMP`/`EXPLO.BMP` lists cover, and the two numbers the audio
 * engine keys its distance models off. This file is the **particle** half: one
 * flat mutable record per thing that has something for `particle-effects.ts` to
 * throw, filled into a caller-owned buffer so a frame's collection allocates
 * nothing.
 *
 * The division is a real one rather than a filing convenience. Everything here
 * exists because a toggle is *on*; everything there is what the port draws
 * either way. `frame-signals.ts` re-exports all of it, so no consumer moved.
 */

import type { Pose, Quat } from "@red-opus/engine";
import { sceneryAlight, SCENERY_TUNABLES } from "@red-opus/engine";
import type { EncounterRunner } from "./sim-runner.ts";
import { airframeDamageFraction } from "./smoke-trail.ts";

/**
 * The slice of an `EncounterRunner` these collectors read. Neither advances
 * anything nor holds a camera, which is what lets a test hand them a hand-made
 * roster; `frame-signals.ts`'s sprite lists take the same slice and re-export
 * this name.
 */
export type FireRunner = Pick<EncounterRunner, "encounter" | "others" | "targets">;

/**
 * How hard a working locomotive smokes, on the same 0-1 scale a damaged
 * airframe uses. Low on purpose: it is a thread of coal smoke over the
 * landscape, not a burning wreck.
 */
export const WORKING_PLUME_FRACTION = 0.3;

/**
 * The particle plume's own rate scale (ENHANCED EFFECTS), on the same 0-1
 * scale `smoke-trail.ts`'s `smokeIntensity` returns for an airframe. Not the
 * damage ladder: coal smoke at the sprite scale above would all but vanish
 * under the 25/137 trip, and a working locomotive — the thing the original's
 * class-5 constructor says you *find* by its plume — deserves a column you can
 * see from patrol height. Port-extension number, CALIBRATION-PENDING.
 */
export const TRAIN_PLUME_INTENSITY = 0.45;

/**
 * Where the coal plume is anchored, in the train's own body frame: metres
 * ahead of the target origin and metres above the railhead. Measured off the
 * shipped stand-in model (`models/TERR1-20`, 92.6 m nose to tail): the origin
 * sits 42.8 m back from the nose, so the smokebox — the one place a locomotive
 * makes smoke — is a hair under 40 m forward of it, at chimney height.
 */
export const TRAIN_STACK_FORWARD_M = 40;
/** Chimney height over the railhead; the model is 6.2 m tall at the cab. */
export const TRAIN_STACK_HEIGHT_M = 4.5;

/**
 * One thing ENHANCED EFFECTS has something to draw for, filled into a
 * caller-owned record so a frame's collection allocates nothing. The name says
 * aeroplane because that is what it was built for, but a **working train**
 * fills the same record (`collectTrainPlumes`): position, travel axes,
 * velocity and a smoke intensity are all a coal plume needs, and `burning:
 * false` keeps it off the flame and spark paths that belong to airframes.
 *
 * The airframe's own axes are carried rather than its quaternion: every
 * consumer wants "two metres ahead of the origin and a little above it", the
 * conversion is the same three lines for all of them, and doing it once here
 * keeps `rotateVec3` — which returns a fresh `Vec3` per call — out of a loop
 * that runs eight times a frame forever.
 */
export interface BurningAircraft {
  id: string;
  x: number;
  y: number;
  z: number;
  /** `BODY_FORWARD` rotated into world space: the nose direction. */
  forwardX: number;
  forwardY: number;
  forwardZ: number;
  /** `BODY_UP` rotated into world space. */
  upX: number;
  upY: number;
  upZ: number;
  /** World velocity, m/s — what a flame slips behind and a puff is left by. */
  vx: number;
  vy: number;
  vz: number;
  /** 0..1 across the whole airframe (`smoke-trail.ts`'s `airframeDamageFraction`). */
  damageFraction: number;
  burning: boolean;
  /**
   * Cumulative rounds from other aeroplanes' guns. The renderer keeps its own
   * copy from last frame and sparks the difference — see `particle-effects.ts`
   * on why this counter is all there is to spark from.
   */
  gunHitsTaken: number;
  /**
   * Replaces `smokeIntensity`'s damage ladder when set. A working train's coal
   * plume is not damage and should not ride the 25/137 trip a hurt airframe
   * does — at the plume's sprite scale (0.3) the ladder would all but silence
   * it, so a train carries its own number (`PARTICLE_TUNABLES.trainPlumeIntensity`).
   */
  plumeIntensity?: number;
  /**
   * Multiplies the birth scatter a flame and a puff are spread over. 1 — the
   * default, and what every airframe and every train uses — is the two-metre
   * ball an aeroplane wears.
   *
   * It exists for the one source that is not two metres across: a **burning
   * building** (AD-018), whose flame front is as wide as its roof. The scale a
   * building passes *is its own half-extent in metres*, which puts the flame
   * over 70 % of the roof (0.35 × half-extent either side of centre) and the
   * smoke column a little wider than the walls (0.55 ×) — the two spread
   * constants in `particle-effects.ts` doing the proportioning rather than a
   * second table of numbers.
   */
  spreadScale?: number;
}

export function emptyBurningAircraft(): BurningAircraft {
  return {
    id: "",
    x: 0, y: 0, z: 0,
    forwardX: 0, forwardY: 0, forwardZ: -1,
    upX: 0, upY: 1, upZ: 0,
    vx: 0, vy: 0, vz: 0,
    damageFraction: 0,
    burning: false,
    gunHitsTaken: 0,
    plumeIntensity: undefined,
    spreadScale: undefined,
  };
}

/**
 * Writes `q`'s world-space nose and up axes into `into`.
 *
 * The two are columns of the rotation matrix `q` represents — the standard
 * quaternion-to-matrix identities, with the nose column negated because
 * `BODY_FORWARD` is −Z (`packages/engine/src/flight/math3d.ts`). Spelled out
 * rather than called through `rotateVec3` so it allocates nothing; pinned
 * against `rotateVec3` in `frame-signals.test.ts`, which is what stops the two
 * from drifting.
 */
function writeAxes(q: Quat, into: BurningAircraft): void {
  const { x, y, z, w } = q;
  into.forwardX = -2 * (x * z + y * w);
  into.forwardY = -2 * (y * z - x * w);
  into.forwardZ = -(1 - 2 * (x * x + y * y));
  into.upX = 2 * (x * y - z * w);
  into.upY = 1 - 2 * (x * x + z * z);
  into.upZ = 2 * (y * z + x * w);
}

/**
 * Fills `into` with every aeroplane **still in the sky** and returns how many it
 * wrote. A destroyed one is dropped for the reason `fireSourcesFor` drops it:
 * the explosion has already played and the wreck's plume is the sprite path's
 * job.
 *
 * **It does not filter to the ones that are alight**, and that is a correction
 * rather than an omission. The first version kept only aeroplanes that were
 * burning or past `smoke.c`'s 25/137 smoke trip, on the reasoning that nothing
 * else has anything to draw. That is true of fire and smoke and false of the
 * third thing the particles do: a **hit spark** fires on a pristine aeroplane
 * the moment a round lands on it, which is the whole point of it, and the
 * filter meant the only aeroplanes that could spark were ones already on fire.
 * Caught on a real GPU by probing the spark path in isolation and counting
 * exactly zero fire-ramp pixels (2026-08-23).
 *
 * The gating still happens — it just happens where it belongs, in
 * `particle-effects.ts`, one condition per effect: `burning` for the flame,
 * `smokeIntensity` for the trail, a rise in `gunHitsTaken` for the sparks. Those
 * are **the same states the sprites and the audio key off**, which is the
 * sight-and-sound rule this file already enforces for the flame and the fire
 * bed. The cost of not filtering here is walking a roster of at most sixteen.
 *
 * Fills rather than returns because this runs every frame for the life of the
 * scene. `into` is sized once by the caller and never again; the count comes
 * back so the caller reads only the prefix that was written this frame.
 */
export function collectBurningAircraft(runner: EncounterRunner, pose: Pose, into: BurningAircraft[]): number {
  let count = 0;
  for (const view of runner.others()) {
    if (view.status === "destroyed") continue;
    const slot = into[count];
    if (!slot) break;
    slot.id = view.id;
    slot.x = view.pose.position.x;
    slot.y = view.pose.position.y;
    slot.z = view.pose.position.z;
    writeAxes(view.pose.orientation, slot);
    slot.vx = view.velocity?.x ?? 0;
    slot.vy = view.velocity?.y ?? 0;
    slot.vz = view.velocity?.z ?? 0;
    slot.damageFraction = view.damageFraction;
    slot.burning = view.burning;
    slot.gunHitsTaken = view.gunHitsTaken ?? 0;
    // An aeroplane never carries a plume override — this also clears a slot a
    // train held last frame, whose stale override would silence the damage ladder.
    slot.plumeIntensity = undefined;
    slot.spreadScale = undefined;
    count += 1;
  }
  const player = runner.encounter().aircraft.find((entry) => entry.ai === null);
  const slot = into[count];
  if (player && slot && player.status !== "destroyed") {
    slot.id = player.id;
    slot.x = pose.position.x;
    slot.y = pose.position.y;
    slot.z = pose.position.z;
    writeAxes(pose.orientation, slot);
    slot.vx = player.sim.velocity.x;
    slot.vy = player.sim.velocity.y;
    slot.vz = player.sim.velocity.z;
    slot.damageFraction = airframeDamageFraction(player.sim.damage);
    slot.burning = player.fire.burning;
    slot.gunHitsTaken = player.gunHitsTaken;
    slot.plumeIntensity = undefined;
    slot.spreadScale = undefined;
    count += 1;
  }
  return count;
}

/**
 * Fills `into` from `offset` with every **working train** — the same records
 * `collectBurningAircraft` writes, for the same zero-allocation reason — and
 * returns the new total. This is the ENHANCED EFFECTS half of the train's coal
 * plume: the sprite path draws it only when the toggle is off
 * (`FrameSourceOptions.omitTrainPlume`), so between the two lists the plume
 * always has exactly one owner.
 *
 * The record is anchored at the **smokebox**, `TRAIN_STACK_FORWARD_M` ahead of
 * the target origin and `TRAIN_STACK_HEIGHT_M` above the rail, so the emitter's
 * own "a little behind the anchor" birth offset puts the first particles just
 * behind the chimney — where a locomotive's smoke actually starts — and the
 * velocity inheritance streams them back over the carriages as the train runs
 * on. Forward is the travel direction: a train's velocity *is* its heading
 * times its one global speed (`combat/targets.ts`'s `createTarget`), so no
 * quaternion is ever needed here.
 *
 * A burning, destroyed or arrived train is not collected: burning is the
 * sprite path's own dying art (out of the toggle's scope, like a gasbag's),
 * and a retired train has banked its fire — `TargetView.smoking` already
 * encodes all three.
 */
export function collectTrainPlumes(runner: FireRunner, into: BurningAircraft[], offset: number): number {
  let count = offset;
  for (const target of runner.targets()) {
    if (!target.smoking) continue;
    const slot = into[count];
    if (!slot) break;
    const speed = Math.hypot(target.velocity.x, target.velocity.y, target.velocity.z);
    // A smoking train always has one (smoking excludes `arrived`), but the
    // guard keeps a hand-made view honest instead of dividing by zero.
    const forwardX = speed > 0 ? target.velocity.x / speed : 0;
    const forwardZ = speed > 0 ? target.velocity.z / speed : 0;
    slot.id = target.id;
    slot.x = target.position.x + forwardX * TRAIN_STACK_FORWARD_M;
    slot.y = target.position.y + TRAIN_STACK_HEIGHT_M;
    slot.z = target.position.z + forwardZ * TRAIN_STACK_FORWARD_M;
    slot.forwardX = forwardX;
    slot.forwardY = 0;
    slot.forwardZ = forwardZ;
    slot.upX = 0;
    slot.upY = 1;
    slot.upZ = 0;
    slot.vx = target.velocity.x;
    slot.vy = target.velocity.y;
    slot.vz = target.velocity.z;
    // The sprite plume's own scale, kept for parity with `smokeSourcesFor`; the
    // particle rate comes from `plumeIntensity`, not from this.
    slot.damageFraction = WORKING_PLUME_FRACTION;
    slot.burning = false;
    slot.gunHitsTaken = 0;
    slot.plumeIntensity = TRAIN_PLUME_INTENSITY;
    slot.spreadScale = undefined;
    count += 1;
  }
  return count;
}

/**
 * How hard a burning building smokes, on `smokeIntensity`'s 0-1 scale.
 *
 * CALIBRATED on the swapchain over the Somme village (2026-08-24). The first
 * pass ran at 0.8 and, with the puffs also scaled to the roof, twelve burning
 * cottages put a **fog bank** over the valley — 10 000 smoke pixels against 15
 * of fire, the column swallowing the fire it was rising off. At 0.45 the same
 * twelve read as twelve separate columns with flame at the foot of each, which
 * is the picture. Port-extension number.
 */
export const SCENERY_PLUME_INTENSITY = 0.45;

/**
 * Metres above the roof the flame is anchored at.
 *
 * Two, and it is not a free number: `particle-effects.ts` throws its flame over
 * `ENGINE_FORWARD_M` = 2 along the source's own forward axis, and a building
 * hands it a forward axis of **straight down**, so an anchor two metres up puts
 * the flame band exactly on the roof and the smoke — born two to four metres
 * *behind* the anchor, which down-forward turns into two to four metres up —
 * in a column above it. The one arrangement that gets a building fire out of
 * the aeroplane emitter without a line of new spawn code.
 */
export const SCENERY_FLAME_ANCHOR_M = 2;

/**
 * Fraction of its life a fire keeps its flame for. Past this it is smoke only:
 * a roof that has finished burning smoulders for a while before it stops, and
 * a flame that vanished at full brightness on one frame would read as a bug.
 */
const SCENERY_FLAME_LIFE_FRACTION = 0.85;
/** Fraction of its life the smoke holds full rate for; it thins linearly over the rest. */
const SCENERY_PLUME_FULL_FRACTION = 0.7;

/**
 * Fills `into` from `offset` with every **burning building** (AD-018) and
 * returns the new total — the same records `collectBurningAircraft` and
 * `collectTrainPlumes` write, for the same zero-allocation reason.
 *
 * Only `sceneryAlight` structures are collected. A record exists from the first
 * round that goes in, but a barn with four holes in it and no fire has nothing
 * to draw, and drawing a wisp for it would tell the pilot he had lit something
 * he had not.
 *
 * **There is no sprite counterpart, and that is the whole toggle contract for
 * this feature.** ENHANCED EFFECTS' rule is replace-not-add, and there is
 * nothing here to replace: the original draws no fire on scenery at all
 * (AD-018 §1), so with the toggle off the picture is byte-for-byte what it was
 * before this lane. The *sim* still tracks the fires either way — it must, or a
 * rendering toggle would change the encounter hash and with it every replay and
 * every lockstep peer.
 */
export function collectSceneryFires(runner: FireRunner, into: BurningAircraft[], offset: number): number {
  let count = offset;
  // `?? []` is not defensive programming against the engine — `EncounterState`
  // always has the field. It is the contract `FireRunner` states: this module's
  // header says these functions take a slice precisely so a test can hand them a
  // hand-made roster, and several do, built before this field existed.
  for (const fire of runner.encounter().sceneryFires ?? []) {
    if (!sceneryAlight(fire)) continue;
    const slot = into[count];
    if (!slot) break;
    const spent = fire.burnSeconds / SCENERY_TUNABLES.burnSeconds;
    slot.id = `scenery-${fire.key}`;
    slot.x = fire.x;
    slot.y = fire.y + SCENERY_FLAME_ANCHOR_M;
    slot.z = fire.z;
    // Forward is straight down and up is up: see `SCENERY_FLAME_ANCHOR_M`.
    slot.forwardX = 0;
    slot.forwardY = -1;
    slot.forwardZ = 0;
    slot.upX = 0;
    slot.upY = 1;
    slot.upZ = 0;
    // A building does not travel, so nothing is inherited and the column stands
    // where it was made instead of streaming off downwind.
    slot.vx = 0;
    slot.vy = 0;
    slot.vz = 0;
    slot.damageFraction = 1;
    slot.burning = spent < SCENERY_FLAME_LIFE_FRACTION;
    // Nothing shoots a building in a way the spark path would know about — the
    // rounds are consumed by `scenery-pass.ts`, not by an aeroplane's counter.
    slot.gunHitsTaken = 0;
    slot.plumeIntensity =
      SCENERY_PLUME_INTENSITY *
      Math.min(1, Math.max(0, (1 - spent) / (1 - SCENERY_PLUME_FULL_FRACTION)));
    slot.spreadScale = fire.spreadM;
    count += 1;
  }
  return count;
}
